import { useRef, useState, type FormEvent } from "react";
import {
  ArrowRight,
  ArrowUpRight,
  Bell,
  Check,
  ChevronDown,
  Code2,
  Copy,
  Download,
  Globe2,
  LayoutDashboard,
  LockKeyhole,
  Monitor,
  Moon,
  Palette,
  Search,
  ShieldCheck,
  Sparkles,
  Sun,
  Upload,
  User,
  Zap,
} from "lucide-react";
import {
  createWorkspace,
  downloadFile,
  importWorkspace,
  type Settings,
} from "../../../core/src";
import type { PageProps } from "../types";
import {
  Avatar,
  Badge,
  Button,
  EmptyState,
  Field,
  IconButton,
  Metric,
  Modal,
  PageHeader,
  Panel,
  Progress,
} from "../components/ui";

export const layouts: {
  id: Settings["layout"];
  name: string;
  description: string;
}[] = [
  {
    id: "sidebar",
    name: "The everyday",
    description: "A clear sidebar. Everything in reach.",
  },
  {
    id: "compact",
    name: "A little less",
    description: "More space for the work that matters.",
  },
  {
    id: "horizontal",
    name: "Across the horizon",
    description: "Navigation above. Possibilities below.",
  },
  {
    id: "dual",
    name: "Two perspectives",
    description: "An icon rail with a separate workspace menu.",
  },
  {
    id: "floating",
    name: "Room to breathe",
    description: "A floating sidebar with a softer silhouette.",
  },
  {
    id: "minimal",
    name: "The essentials",
    description: "Just your work and a compact top navigation.",
  },
  {
    id: "wide",
    name: "Think bigger",
    description: "An expanded canvas for detailed work.",
  },
  {
    id: "boxed",
    name: "In good company",
    description: "A centered workspace with a little breathing room.",
  },
  {
    id: "rail",
    name: "On the side",
    description: "A narrow navigation rail. A wider perspective.",
  },
  {
    id: "dark-sidebar",
    name: "A different shade",
    description: "Graphite navigation with a bright workspace.",
  },
];
export const accents = {
  blue: "#4263eb",
  emerald: "#087f68",
  indigo: "#7e69ba",
  rose: "#bd426e",
  amber: "#a56413",
};
export const DEFAULT_CUSTOM_ACCENT = "#7e69ba";
export const accentKey = (accent: Settings["accent"]) =>
  accent === "lime" ? "blue" : accent;
export const accentValue = (settings: Settings) => {
  const key = accentKey(settings.accent);
  return key === "custom"
    ? (settings.customAccent ?? DEFAULT_CUSTOM_ACCENT)
    : accents[key];
};
export const accentInk = (hex: string) => {
  const channels = [1, 3, 5].map((start) => {
    const value = Number.parseInt(hex.slice(start, start + 2), 16) / 255;
    return value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  });
  const luminance =
    channels[0] * 0.2126 + channels[1] * 0.7152 + channels[2] * 0.0722;
  return luminance > 0.46 ? "#18202e" : "#ffffff";
};

export function AccentPicker({
  settings,
  onChange,
}: {
  settings: Settings;
  onChange: (partial: Partial<Settings>) => void;
}) {
  const customColor = settings.customAccent ?? DEFAULT_CUSTOM_ACCENT;
  const [valid, setValid] = useState(true);
  const selected = accentKey(settings.accent);
  const useCustom = (value: string) => {
    const normalized = value.toLowerCase();
    setValid(true);
    onChange({ accent: "custom", customAccent: normalized });
  };
  return (
    <div className="accent-picker">
      <div className="accent-choices">
        {Object.entries(accents).map(([key, color]) => (
          <button
            type="button"
            aria-label={`Use ${key} accent`}
            aria-pressed={selected === key}
            key={key}
            style={{ backgroundColor: color }}
            onClick={() => onChange({ accent: key as Settings["accent"] })}
          >
            {selected === key && <Check size={20} />}
          </button>
        ))}
        <label
          className={`accent-custom-swatch ${selected === "custom" ? "selected" : ""}`}
          title="Choose any accent colour"
        >
          <input
            type="color"
            aria-label="Choose a custom accent colour"
            value={customColor}
            onChange={(event) => useCustom(event.target.value)}
          />
          <span
            style={{
              backgroundColor: customColor,
              color: accentInk(customColor),
            }}
          >
            <Palette size={15} />
          </span>
        </label>
      </div>
      <form
        className="accent-custom-row"
        onSubmit={(event) => {
          event.preventDefault();
          const value = String(
            new FormData(event.currentTarget).get("customAccent") ?? "",
          ).trim();
          if (/^#[0-9a-f]{6}$/i.test(value)) useCustom(value);
          else setValid(false);
        }}
      >
        <div>
          <strong>Custom colour</strong>
          <small>Pick visually or enter a six-digit hex value.</small>
        </div>
        <input
          className="input accent-custom-input"
          key={customColor}
          name="customAccent"
          aria-label="Custom accent hex colour"
          aria-invalid={!valid}
          defaultValue={customColor}
          maxLength={7}
          spellCheck={false}
          onChange={(event) =>
            setValid(/^#[0-9a-f]{6}$/i.test(event.target.value.trim()))
          }
        />
        <Button type="submit" variant="secondary" disabled={!valid}>
          Use colour
        </Button>
      </form>
    </div>
  );
}
export const colorways: {
  id: NonNullable<Settings["colorway"]>;
  name: string;
  description: string;
}[] = [
  {
    id: "pearl",
    name: "Pearl",
    description: "Bright, neutral and quietly polished.",
  },
  {
    id: "lavender",
    name: "Lavender",
    description: "Soft violet depth with an editorial feel.",
  },
  {
    id: "sand",
    name: "Sand",
    description: "Warm, calm and naturally understated.",
  },
  {
    id: "graphite",
    name: "Graphite",
    description: "High contrast for a focused workspace.",
  },
  {
    id: "sage",
    name: "Sage",
    description: "A restorative botanical green with quiet depth.",
  },
  {
    id: "blush",
    name: "Blush",
    description: "Warm rose neutrals with a refined softness.",
  },
  {
    id: "ocean",
    name: "Ocean",
    description: "Clear teal tones with a calm, modern energy.",
  },
  {
    id: "clay",
    name: "Clay",
    description: "Earthy terracotta with an architectural warmth.",
  },
];
export const colorwayKey = (colorway: Settings["colorway"]) =>
  colorway ?? "lavender";

export function SettingsPage({ state, mutate, notify }: PageProps) {
  const [tab, setTab] = useState("profile");
  const [reset, setReset] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const update = (partial: Partial<Settings>) =>
    mutate((s) => ({ ...s, settings: { ...s.settings, ...partial } }));
  const saveProfile = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    if (
      !update({
        name: String(f.get("name")).trim(),
        email: String(f.get("email")).trim(),
        workspace: String(f.get("workspace")).trim(),
        role: String(f.get("role")).trim(),
      })
    )
      return;
    notify("Your profile is up to date.");
  };
  const importData = async (file?: File) => {
    if (!file) return;
    try {
      if (file.size > 5_000_000)
        throw new Error("Backup must be smaller than 5 MB.");
      const next = importWorkspace(await file.text());
      if (!mutate(() => next)) return;
      notify("Workspace restored from your backup.");
    } catch (error) {
      notify(
        error instanceof Error
          ? error.message
          : "This backup could not be read.",
        "error",
      );
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };
  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="MAKE YOURSELF AT HOME"
        title="Your space. Your way."
        description="The little details that make this workspace feel like yours."
      />
      <div className="settings-layout">
        <nav className="settings-nav" aria-label="Settings sections">
          {[
            { id: "profile", label: "My profile", icon: User },
            { id: "appearance", label: "Appearance", icon: Palette },
            { id: "preferences", label: "Preferences", icon: Globe2 },
            { id: "data", label: "Workspace data", icon: ShieldCheck },
          ].map((item) => (
            <button
              key={item.id}
              className={tab === item.id ? "active" : ""}
              onClick={() => setTab(item.id)}
            >
              <item.icon size={17} />
              {item.label}
              <ArrowRight size={14} />
            </button>
          ))}
        </nav>
        <div>
          {tab === "profile" && (
            <Panel
              title="Personal details"
              description="How you show up in your workspace"
            >
              <div className="profile-preview">
                <Avatar name={state.settings.name} color="#DEE5F4" size="lg" />
                <div>
                  <h3>{state.settings.name}</h3>
                  <p>{state.settings.role}</p>
                  <Badge tone="neutral">Workspace profile</Badge>
                </div>
              </div>
              <form onSubmit={saveProfile}>
                <div className="form-grid">
                  <Field label="Full name">
                    <input
                      className="input"
                      name="name"
                      defaultValue={state.settings.name}
                      required
                      maxLength={100}
                    />
                  </Field>
                  <Field label="Email">
                    <input
                      className="input"
                      name="email"
                      type="email"
                      maxLength={254}
                      defaultValue={state.settings.email}
                      required
                    />
                  </Field>
                  <Field label="Workspace name">
                    <input
                      className="input"
                      name="workspace"
                      defaultValue={state.settings.workspace}
                      required
                      maxLength={100}
                    />
                  </Field>
                  <Field label="Your role">
                    <input
                      className="input"
                      name="role"
                      defaultValue={state.settings.role}
                      required
                      maxLength={100}
                    />
                  </Field>
                </div>
                <div className="form-actions">
                  <Button variant="primary" type="submit">
                    <Check size={16} />
                    Save changes
                  </Button>
                </div>
              </form>
            </Panel>
          )}
          {tab === "appearance" && (
            <div className="page-stack">
              <Panel
                title="Your workspace atmosphere"
                description="Choose the colour foundation that feels most natural"
              >
                <div className="colorway-choices colorway-choices--wide">
                  {colorways.map((colorway) => (
                    <button
                      key={colorway.id}
                      className={
                        colorwayKey(state.settings.colorway) === colorway.id
                          ? "selected"
                          : ""
                      }
                      aria-pressed={
                        colorwayKey(state.settings.colorway) === colorway.id
                      }
                      onClick={() => update({ colorway: colorway.id })}
                    >
                      <span
                        className={`colorway-swatch colorway-swatch--${colorway.id}`}
                      >
                        <i />
                        <b />
                        <em />
                      </span>
                      <strong>
                        {colorway.name}
                        {colorwayKey(state.settings.colorway) ===
                          colorway.id && <Check size={14} />}
                      </strong>
                      <small>{colorway.description}</small>
                    </button>
                  ))}
                </div>
              </Panel>
              <Panel
                title="Your signature colour"
                description="Choose a preset or make the accent entirely your own"
              >
                <AccentPicker settings={state.settings} onChange={update} />
              </Panel>
              <Panel
                title="A different point of view"
                description="Choose a theme that feels right"
              >
                <div className="theme-choices">
                  {(
                    [
                      { id: "light", label: "Light", icon: Sun },
                      { id: "dark", label: "Dark", icon: Moon },
                      { id: "system", label: "System", icon: Monitor },
                    ] as const
                  ).map((theme) => (
                    <button
                      key={theme.id}
                      className={`theme-choice ${state.settings.theme === theme.id ? "selected" : ""}`}
                      onClick={() => update({ theme: theme.id })}
                    >
                      <div className={`theme-swatch theme-swatch--${theme.id}`}>
                        <span />
                        <div>
                          <i />
                          <i />
                          <i />
                        </div>
                      </div>
                      <span>
                        <theme.icon size={16} />
                        {theme.label}
                        {state.settings.theme === theme.id && (
                          <Check size={15} />
                        )}
                      </span>
                    </button>
                  ))}
                </div>
              </Panel>
              <Panel
                title="A little more space"
                description="Choose how much breathing room you like"
              >
                <div className="segmented">
                  <button
                    className={
                      state.settings.density === "comfortable" ? "active" : ""
                    }
                    onClick={() => update({ density: "comfortable" })}
                  >
                    Comfortable
                  </button>
                  <button
                    className={
                      state.settings.density === "compact" ? "active" : ""
                    }
                    onClick={() => update({ density: "compact" })}
                  >
                    Compact
                  </button>
                </div>
              </Panel>
            </div>
          )}
          {tab === "preferences" && (
            <Panel
              title="Everyday preferences"
              description="Small adjustments for a better flow"
            >
              <div className="form-grid">
                <Field
                  label="Navigation language"
                  hint="Navigation labels are translated; demo content remains in English."
                >
                  <select
                    className="select"
                    value={state.settings.locale}
                    onChange={(e) =>
                      update({
                        locale: e.target.value as Settings["locale"],
                        direction: e.target.value === "ar" ? "rtl" : "ltr",
                      })
                    }
                  >
                    <option value="en">English</option>
                    <option value="fr">Français</option>
                    <option value="ar">العربية</option>
                  </select>
                </Field>
                <Field label="Text direction">
                  <select
                    className="select"
                    value={state.settings.direction}
                    onChange={(e) =>
                      update({
                        direction: e.target.value as Settings["direction"],
                      })
                    }
                  >
                    <option value="ltr">Left to right</option>
                    <option value="rtl">Right to left</option>
                  </select>
                </Field>
                <Field
                  label="Display currency"
                  hint="Changes the currency label; amounts are not converted."
                >
                  <select
                    className="select"
                    value={state.settings.currency}
                    onChange={(e) =>
                      update({
                        currency: e.target.value as Settings["currency"],
                      })
                    }
                  >
                    <option value="USD">USD — US dollar</option>
                    <option value="EUR">EUR — Euro</option>
                    <option value="GBP">GBP — British pound</option>
                  </select>
                </Field>
                <Field label="Calendar week">
                  <select
                    className="select"
                    value={String(state.settings.weekStartsMonday)}
                    onChange={(e) =>
                      update({ weekStartsMonday: e.target.value === "true" })
                    }
                  >
                    <option value="true">Starts on Monday</option>
                    <option value="false">Starts on Sunday</option>
                  </select>
                </Field>
              </div>
              <div className="setting-row">
                <div>
                  <h3>Activity notifications</h3>
                  <p>Display in-app activity notifications.</p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={state.settings.notifications}
                  aria-label="Activity notifications"
                  className={`switch ${state.settings.notifications ? "on" : ""}`}
                  onClick={() =>
                    update({ notifications: !state.settings.notifications })
                  }
                >
                  <span />
                </button>
              </div>
              <p className="settings-note">
                <Check size={14} />
                Preferences are saved automatically.
              </p>
            </Panel>
          )}
          {tab === "data" && (
            <div className="page-stack">
              <Panel
                title="Your workspace, backed up"
                description="Keep a portable copy of your contacts, projects and settings"
              >
                <div className="data-actions">
                  <div className="data-action">
                    <span className="utility-icon">
                      <Download size={22} />
                    </span>
                    <div>
                      <h3>Export workspace</h3>
                      <p>Download all your workspace data as a JSON backup.</p>
                    </div>
                    <Button
                      onClick={() => {
                        downloadFile(
                          `elyxir-backup-${new Date().toISOString().slice(0, 10)}.json`,
                          JSON.stringify(state, null, 2),
                          "application/json",
                        );
                        notify("Workspace backup downloaded.");
                      }}
                    >
                      Export
                    </Button>
                  </div>
                  <div className="data-action">
                    <span className="utility-icon">
                      <Upload size={22} />
                    </span>
                    <div>
                      <h3>Restore a backup</h3>
                      <p>
                        A valid Elyxir backup replaces the current workspace.
                      </p>
                    </div>
                    <Button onClick={() => fileRef.current?.click()}>
                      Import
                    </Button>
                    <input
                      ref={fileRef}
                      className="sr-only"
                      type="file"
                      accept="application/json,.json"
                      aria-label="Import workspace backup"
                      onChange={(e) => void importData(e.target.files?.[0])}
                    />
                  </div>
                </div>
              </Panel>
              <Panel
                title="Start with a fresh canvas"
                description="Reset this workspace to the original fictional sample data"
              >
                <Button variant="danger" onClick={() => setReset(true)}>
                  Reset demo workspace
                </Button>
              </Panel>
              <Panel title="About this workspace">
                <div className="key-value-list">
                  <div>
                    <span>Product</span>
                    <strong>Elyxir Workspace</strong>
                  </div>
                  <div>
                    <span>Version</span>
                    <strong>0.1.0</strong>
                  </div>
                  <div>
                    <span>Data</span>
                    <strong>Fictional examples, editable by you</strong>
                  </div>
                  <div>
                    <span>External services</span>
                    <strong>No email or payment provider connected</strong>
                  </div>
                </div>
              </Panel>
            </div>
          )}
        </div>
      </div>
      <Modal
        open={reset}
        onClose={() => setReset(false)}
        title="Start fresh?"
        description="Your current workspace data will be replaced. Export a backup first if you want to keep it."
      >
        <div className="form-actions">
          <Button onClick={() => setReset(false)}>Keep my workspace</Button>
          <Button
            variant="danger"
            onClick={() => {
              if (!mutate(() => createWorkspace())) return;
              setReset(false);
              notify("A fresh workspace is ready.");
            }}
          >
            Reset workspace
          </Button>
        </div>
      </Modal>
    </div>
  );
}

export function StudioPage({ state, mutate, notify }: PageProps) {
  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="ONE WORKSPACE. YOUR PERSPECTIVE."
        title="Find your flow."
        description="Ten layout arrangements. The same thoughtful tools. Make it your own."
      />
      <div className="studio-banner">
        <div>
          <Palette size={21} />
          <h2>Designed to work beautifully, together.</h2>
          <p>
            Every layout shares your data, preferences and application modules.
          </p>
        </div>
        <Badge tone="green">{layouts.length} layouts</Badge>
      </div>
      <div className="layout-grid">
        {layouts.map((layout, i) => (
          <button
            key={layout.id}
            className={`layout-card ${state.settings.layout === layout.id ? "selected" : ""}`}
            onClick={() => {
              if (
                !mutate((s) => ({
                  ...s,
                  settings: { ...s.settings, layout: layout.id },
                }))
              )
                return;
              notify(`${layout.name} layout applied.`);
            }}
          >
            <div className={`layout-mini layout-mini--${layout.id}`}>
              <aside>
                <i />
                <i />
                <i />
                <i />
              </aside>
              <div>
                <header>
                  <i />
                  <b />
                </header>
                <section>
                  <span />
                  <span />
                  <span />
                </section>
                <main>
                  <article />
                  <article />
                </main>
                <footer>
                  <i />
                  <i />
                </footer>
              </div>
            </div>
            <div className="layout-card-caption">
              <div>
                <small>LAYOUT {String(i + 1).padStart(2, "0")}</small>
                <h3>{layout.name}</h3>
                <p>{layout.description}</p>
              </div>
              {state.settings.layout === layout.id ? (
                <Check size={20} />
              ) : (
                <ArrowUpRight size={20} />
              )}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

export function LibraryPage({ notify }: PageProps) {
  const [tab, setTab] = useState("components");
  const [dialog, setDialog] = useState(false);
  const [checked, setChecked] = useState(true);
  const [accordion, setAccordion] = useState(0);
  const [demoTab, setDemoTab] = useState("Overview");
  const [slider, setSlider] = useState(64);
  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      notify("Copied to clipboard.");
    } catch {
      notify("Clipboard is unavailable in this browser.", "error");
    }
  };
  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="THE DETAILS MAKE THE DIFFERENCE"
        title="A thoughtful foundation."
        description="Consistent components. Clear patterns. A little less work for your next idea."
        actions={
          <Button
            onClick={() =>
              copy(
                "import { Button, Panel, Modal } from './packages/ui/src/components/ui';",
              )
            }
          >
            <Copy size={16} />
            Copy import
          </Button>
        }
      />
      <div className="filter-tabs section-tabs">
        {["components", "tokens", "getting started"].map((t) => (
          <button
            key={t}
            className={tab === t ? "active" : ""}
            onClick={() => setTab(t)}
          >
            {t}
          </button>
        ))}
      </div>
      {tab === "components" && (
        <div className="library-grid">
          <Panel
            title="Buttons"
            description="Clear actions, consistent hierarchy"
          >
            <div className="component-row">
              <Button
                variant="primary"
                onClick={() => notify("Primary action completed.")}
              >
                <PlusIcon />
                Create something
              </Button>
              <Button onClick={() => notify("Secondary action completed.")}>
                Secondary
              </Button>
              <Button variant="ghost" onClick={() => notify("Quietly done.")}>
                A quieter action <ArrowUpRight size={15} />
              </Button>
              <Button
                variant="danger"
                onClick={() =>
                  notify("Destructive style preview only.", "info")
                }
              >
                Destructive
              </Button>
              <Button disabled>Unavailable</Button>
            </div>
          </Panel>
          <Panel
            title="Status & identity"
            description="A little context goes a long way"
          >
            <div className="component-row">
              <Badge tone="green">Active</Badge>
              <Badge tone="blue">In progress</Badge>
              <Badge tone="amber">Pending</Badge>
              <Badge tone="red">Attention</Badge>
              <Badge tone="purple">New</Badge>
            </div>
            <div className="component-row">
              <Avatar name="Alex Morgan" size="lg" />
              <Avatar name="Jamie Chen" color="#E2DCF6" />
              <Avatar name="Sam Rivera" color="#FAE4D2" size="sm" />
              <div className="avatar-stack">
                <Avatar name="A M" />
                <Avatar name="J C" color="#DEDDF3" />
                <Avatar name="S R" color="#EBD6C5" />
              </div>
            </div>
          </Panel>
          <Panel
            title="Inputs & controls"
            description="Make every interaction feel natural"
          >
            <div className="form-grid">
              <Field label="Email address">
                <input
                  className="input"
                  type="email"
                  placeholder="you@company.com"
                />
              </Field>
              <Field label="Project status">
                <select className="select">
                  <option>In progress</option>
                  <option>In review</option>
                  <option>Complete</option>
                </select>
              </Field>
            </div>
            <div className="component-row">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={(e) => setChecked(e.target.checked)}
                />
                Keep me in the loop
              </label>
              <button
                className={`switch ${checked ? "on" : ""}`}
                role="switch"
                aria-label="Sample toggle"
                aria-checked={checked}
                onClick={() => setChecked(!checked)}
              >
                <span />
              </button>
            </div>
            <Field label={`Progress: ${slider}%`}>
              <input
                type="range"
                min="0"
                max="100"
                value={slider}
                onChange={(e) => setSlider(Number(e.target.value))}
              />
            </Field>
            <Progress label="Example progress" value={slider} />
          </Panel>
          <Panel
            title="Tabs & disclosure"
            description="Show the right information at the right time"
          >
            <div className="segmented">
              {["Overview", "Details", "Activity"].map((t) => (
                <button
                  aria-pressed={t === demoTab}
                  key={t}
                  className={demoTab === t ? "active" : ""}
                  onClick={() => setDemoTab(t)}
                >
                  {t}
                </button>
              ))}
            </div>
            <p className="component-tab-content">
              {demoTab === "Overview"
                ? "Everything you need, in one clear view."
                : demoTab === "Details"
                  ? "The details that make the bigger picture possible."
                  : "A little history of how your work is moving."}
            </p>
            {[
              "What’s included in Elyxir?",
              "Can I customize the design?",
              "Does it work on mobile?",
            ].map((q, i) => (
              <div className="accordion-item" key={q}>
                <button
                  aria-expanded={accordion === i}
                  onClick={() => setAccordion(accordion === i ? -1 : i)}
                >
                  {q}
                  <ChevronDown size={16} />
                </button>
                {accordion === i && (
                  <p>
                    {
                      [
                        "A shared application, reusable React components, portable integrations, sample workflows and documentation.",
                        "Yes. Theme tokens, typography, spacing, accents and layouts are defined in the shared stylesheet.",
                        "Layouts adapt to smaller screens with an accessible menu, responsive cards and scrollable data tables.",
                      ][i]
                    }
                  </p>
                )}
              </div>
            ))}
          </Panel>
          <Panel
            title="Feedback & overlays"
            description="Make the next step feel obvious"
          >
            <div className="component-row">
              <Button onClick={() => setDialog(true)}>
                Open dialog <ArrowUpRight size={16} />
              </Button>
              <Button
                onClick={() =>
                  notify("Everything is saved. You’re good to go.")
                }
              >
                <Check size={16} />
                Success toast
              </Button>
              <Button
                onClick={() =>
                  notify("Something needs your attention.", "error")
                }
              >
                Error toast
              </Button>
            </div>
            <div className="inline-alert">
              <ShieldCheck size={19} />
              <div>
                <strong>You’re in good hands.</strong>
                <p>
                  Dialogs support keyboard navigation, focus trapping and Escape
                  to close.
                </p>
              </div>
            </div>
          </Panel>
          <Panel
            title="Empty states"
            description="Every beginning deserves a little guidance"
          >
            <EmptyState
              icon={<FolderIcon />}
              title="A space for something great"
              description="Your next project could be the start of something remarkable."
              action={
                <Button
                  onClick={() =>
                    notify(
                      "This is the reusable empty-state component.",
                      "info",
                    )
                  }
                >
                  Start a project <ArrowRight size={15} />
                </Button>
              }
            />
          </Panel>
        </div>
      )}
      {tab === "tokens" && (
        <div className="page-stack">
          <Panel
            title="A calm, considered palette"
            description="A neutral foundation with a distinctive touch"
          >
            <div className="token-grid">
              {[
                { label: "Canvas", color: "var(--canvas)", code: "--canvas" },
                {
                  label: "Surface",
                  color: "var(--surface)",
                  code: "--surface",
                },
                {
                  label: "Graphite",
                  color: "var(--sidebar)",
                  code: "--sidebar",
                },
                {
                  label: "Electric blue",
                  color: "var(--accent)",
                  code: "--accent",
                },
                { label: "Muted", color: "var(--muted)", code: "--muted" },
                { label: "Outline", color: "var(--border)", code: "--border" },
              ].map((token) => (
                <button
                  className="token-swatch"
                  key={token.code}
                  onClick={() => copy(`var(${token.code})`)}
                >
                  <span style={{ background: token.color }} />
                  <strong>{token.label}</strong>
                  <code>{token.code}</code>
                </button>
              ))}
            </div>
          </Panel>
          <Panel
            title="Typography"
            description="DM Sans · locally bundled, no remote font requests"
          >
            <div className="type-specimen">
              <h1>A little clarity.</h1>
              <h2>A lot of possibility.</h2>
              <h3>Room for your next big thing.</h3>
              <p>
                Good design gives your work room to breathe. Clear hierarchy,
                balanced proportions, and thoughtful spacing make it easier to
                focus on what matters.
              </p>
              <span className="eyebrow">THE DETAILS MAKE THE DIFFERENCE</span>
            </div>
          </Panel>
        </div>
      )}
      {tab === "getting started" && (
        <div className="page-stack">
          <Panel
            title="Make it yours"
            description="A clear starting point for your next application"
          >
            <ol className="guide-steps">
              <li>
                <span>01</span>
                <div>
                  <h3>Run the workspace</h3>
                  <p>
                    Install dependencies and start the development server from
                    the Elyxir folder.
                  </p>
                  <code>npm install &amp;&amp; npm run dev</code>
                </div>
              </li>
              <li>
                <span>02</span>
                <div>
                  <h3>Choose your edition</h3>
                  <p>
                    React and Next.js use the shared React application. Other
                    framework hosts embed the portable custom element. See
                    editions/ for exact setup and integration scope.
                  </p>
                </div>
              </li>
              <li>
                <span>03</span>
                <div>
                  <h3>Connect your data</h3>
                  <p>
                    The default demo saves locally in your browser. An optional
                    Express/SQLite API provides sessions, validation and
                    persistence. Follow server/README.md to configure it.
                  </p>
                </div>
              </li>
              <li>
                <span>04</span>
                <div>
                  <h3>Build and package</h3>
                  <p>
                    Create the standalone showcase and portable bundle, then
                    synchronize host integrations.
                  </p>
                  <code>npm run build &amp;&amp; npm run edition:sync</code>
                </div>
              </li>
            </ol>
          </Panel>
          <Panel title="A clear picture of what’s included">
            <p className="documentation-copy">
              This is an original application toolkit with working sample
              workflows. Demo billing, invitations and mail do not contact
              external services. Framework support and release readiness are
              recorded in the documentation. The shipped source, generated
              assets and third-party notices define the complete product.
            </p>
          </Panel>
        </div>
      )}
      <Modal
        open={dialog}
        onClose={() => setDialog(false)}
        title="A little space to focus"
        description="A reusable dialog for meaningful actions."
      >
        <Field label="Name your next idea">
          <input className="input" placeholder="Something worth building…" />
        </Field>
        <div className="form-actions">
          <Button onClick={() => setDialog(false)}>Maybe later</Button>
          <Button
            variant="primary"
            onClick={() => {
              setDialog(false);
              notify("A fresh start. Your dialog is working.");
            }}
          >
            Let’s begin <ArrowRight size={16} />
          </Button>
        </div>
      </Modal>
    </div>
  );
}
function PlusIcon() {
  return (
    <span aria-hidden="true" style={{ fontSize: 20, lineHeight: 1 }}>
      +
    </span>
  );
}
function FolderIcon() {
  return <LayoutDashboard size={24} />;
}
